import { EditorState } from 'prosemirror-state'
import { DecorationSet } from 'prosemirror-view'

import { MoresContds } from '#util/formats'
import { PaginationBreakdown } from '#util/pagination'

import { actSlugTrackerKey } from '../act-slug-tracker'
import { getSpeaker } from '../block-info/speakerLookup'
import {
  BoundaryKind,
  buildPaginationDecorations,
  buildRevisionRailDecorations,
  PageDecorationBuildOptions,
  type PageDecorationData,
} from '../page-decoration'
import { highestRevisionForRange, getLoadedRevisionsState } from '../revisions'

import { ScriptContext } from './inlinePageBreaksPlugin'

export function buildDecorationsFromPageDecoration(
  breakdown: PaginationBreakdown,
  editorState: EditorState,
  scriptContext?: ScriptContext,
  moresContds?: MoresContds,
): DecorationSet {
  const actSlugTracker = actSlugTrackerKey.getState(editorState)
  const revisionsState = getLoadedRevisionsState(editorState)

  const pages: PageDecorationData[] = []

  // build page decorations for title page node if exists
  for (const page of breakdown.titlePageBreakdown?.decorationData ?? []) {
    const titlePageData: PageDecorationData = {
      start: page.startPos,
      end: page.endPos,
      shortfall: page.heightShortfall,
      boundaryKind: BoundaryKind.Normal,
      pageNumber: page.pageNumber,
      isLockedPageBreak: page.isLockedPageBreak,
      isManualBreak: page.isManualBreak,
      isTitlePage: true,
    }
    pages.push(titlePageData)
  }

  // build page decorations for page node
  for (let index = 0; index < breakdown.decorationData.length; index++) {
    const page = breakdown.decorationData[index]
    const pageBreakInfo = breakdown.pageBreaks[index]
    const { act, slug } = actSlugTracker?.getActAndSlugAt(page.endPos - 1) ?? {}
    const latestRevision = highestRevisionForRange(
      editorState,
      page.startPos,
      page.endPos,
    )

    let pageSection = ''
    if (act) pageSection = act.toUpperCase()
    if (slug) pageSection = `${pageSection} – ${slug}`

    pages.push({
      start: page.startPos,
      end: page.endPos,
      // for last page set shortfall to 0
      shortfall:
        index === breakdown.decorationData.length - 1
          ? 0
          : page.heightShortfall,
      boundaryKind: page.isDialogueBreak
        ? BoundaryKind.DialogueContinuation
        : BoundaryKind.Normal,
      pageNumber: page.pageNumber,
      pageAlias: page.pageAlias,
      isLockedPageBreak: page.isLockedPageBreak,
      isManualBreak: page.isManualBreak,
      pageSection,
      sceneContdHeader: pageBreakInfo?.sceneContdHeader,
      sceneContdFooter: pageBreakInfo?.sceneContdFooter,
      sceneNumber: pageBreakInfo?.sceneNumber,
      sceneElementNumber: pageBreakInfo?.sceneElementNumber,
      latestRevision,
    })
  }

  const options: PageDecorationBuildOptions = {
    pageMeta: scriptContext?.pageMeta,
    moresContds,
    totalPages: breakdown.pageCount,
    scriptMeta: {
      title: scriptContext?.scriptTitle,
      timestamp: scriptContext?.timestamp,
      date: scriptContext?.date,
      duration: scriptContext?.duration,
      activeRevision: revisionsState?.revisions.active,
    },
    callbacks: {
      getSpeaker: (pos) => getSpeaker(editorState, pos),
    },
  }

  let decorations = buildPaginationDecorations(editorState.doc, pages, options)

  if (revisionsState?.showPageColors) {
    decorations = decorations.add(
      editorState.doc,
      buildRevisionRailDecorations(pages),
    )
  }

  return decorations
}
